import { createFileRoute, notFound } from '@tanstack/react-router'
import { validBrandScope } from '../features/brands/api'
import { resolveBrandScope, useBrandScope } from '../features/brands/use-brand-scope'
import { notificationPrefsQuery } from '../features/me/queries'
import {
  laneDefinition,
  needsUsFilter,
  normaliseQueueRouteLane,
  QueueScreen,
  queueLaneQuery,
  queueLanesQuery,
  queueNeedsUsNowQuery,
  queueParkedQuery,
  rememberedLaneSort,
} from '../features/queue'
import { sessionQuery } from '../lib/api/queries'

export const Route = createFileRoute('/queue/$lane')({
  validateSearch: (search: Record<string, unknown>): { brand?: string; focus?: string; notification?: string; view?: 'parked' } => ({
    brand: validBrandScope(search.brand),
    focus: typeof search.focus === 'string' && /^(card|flag):[0-9a-f-]{36}$/i.test(search.focus) ? search.focus : undefined,
    notification: typeof search.notification === 'string' && /^[0-9a-f-]{36}$/i.test(search.notification) ? search.notification : undefined,
    view: search.view === 'parked' ? 'parked' : undefined,
  }),
  loaderDeps: ({ search }) => ({ brand: search.brand, view: search.view }),
  loader: async ({ context, params, deps }) => {
    const lane = normaliseQueueRouteLane(params.lane)
    if (!lane) throw notFound()
    const session = context.queryClient.getQueryData(sessionQuery().queryKey)
    const brand = resolveBrandScope(session?.me ?? null, deps.brand).param
    if (lane === 'signals' && deps.view === 'parked') {
      await Promise.allSettled([
        context.queryClient.ensureQueryData(queueLanesQuery(brand)),
        context.queryClient.ensureQueryData(queueParkedQuery(brand)),
      ])
      return
    }
    const needsFilter = needsUsFilter(lane)
    if (needsFilter) {
      await Promise.allSettled([
        context.queryClient.ensureQueryData(queueLanesQuery(brand)),
        context.queryClient.ensureInfiniteQueryData(queueNeedsUsNowQuery(needsFilter, brand)),
      ])
      return
    }
    const apiLane = laneDefinition(lane).api
    const prefs = await context.queryClient.ensureQueryData(notificationPrefsQuery()).catch(() => undefined)
    const query = queueLaneQuery(apiLane, rememberedLaneSort(prefs, apiLane), brand)
    await Promise.allSettled([
      context.queryClient.ensureQueryData(queueLanesQuery(brand)),
      context.queryClient.ensureInfiniteQueryData(query),
    ])
  },
  component: function QueueRoute() {
    const params = Route.useParams()
    const search = Route.useSearch()
    const brandScope = useBrandScope()
    const lane = normaliseQueueRouteLane(params.lane)
    if (!lane) return null
    return (
      <QueueScreen
        key={`${lane}:${search.view ?? 'cases'}:${brandScope.selected}`}
        lane={lane}
        focus={search.focus}
        parked={search.view === 'parked'}
        brand={brandScope.param}
      />
    )
  },
})
